// ============================================================================
// Customer Subscription Types (Legacy/Basic)
// ============================================================================

/**
 * Tier of customer subscription (for membership plans)
 * This is the canonical definition - membership.ts imports from here
 */
export enum SubscriptionTier {
  Basic = 'Basic',
  Standard = 'Standard',
  Premium = 'Premium',
  Family = 'Family'
}

/**
 * Status of a customer subscription
 */
export enum SubscriptionStatus {
  Active = 'Active',
  Cancelled = 'Cancelled',
  PastDue = 'PastDue',
  Incomplete = 'Incomplete',
  IncompleteExpired = 'IncompleteExpired',
  Trialing = 'Trialing',
  Unpaid = 'Unpaid'
}

/**
 * Customer subscription details
 */
export interface Subscription {
  id: number;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  monthlyPrice: number;
  cutsPerMonth: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  userName: string;
  userEmail: string;
  remainingCuts: number;
  usedCuts: number;
  downgradeEffectiveAt?: string;
  pendingDowngradeTier?: SubscriptionTier;
}

/**
 * DTO for subscription usage tracking
 */
export interface SubscriptionUsageDto {
  subscriptionId: number;
  month: number;
  year: number;
  usageCount: number;
  allowedCount: number;
  remainingCount: number;
  tier: SubscriptionTier;
}

// ============================================================================
// Platform Subscription Types
// ============================================================================

/**
 * Type of platform subscription
 */
export enum PlatformSubscriptionType {
  /** Shop Owner subscription: $100/month base + $20/month per additional barber (5 included) */
  ShopOwner = 'ShopOwner',
  /** Barber subscription: $40/month flat rate for unlimited customers */
  Barber = 'Barber'
}

/**
 * Status of the platform subscription
 */
export enum PlatformSubscriptionStatus {
  /** Subscription is active and in good standing */
  Active = 'Active',
  /** Subscription has been cancelled but still active until period end */
  Cancelled = 'Cancelled',
  /** Payment failed, subscription past due */
  PastDue = 'PastDue',
  /** Subscription is in trial period */
  Trialing = 'Trialing',
  /** Subscription payment incomplete */
  Incomplete = 'Incomplete',
  /** Subscription incomplete and expired */
  IncompleteExpired = 'IncompleteExpired',
  /** Subscription has unpaid invoices */
  Unpaid = 'Unpaid',
  /** Subscription has been permanently cancelled and is no longer active */
  Expired = 'Expired'
}

/**
 * Response DTO for platform subscription details
 */
export interface PlatformSubscriptionDto {
  id: number;
  /** Type of subscription (ShopOwner or Barber) */
  type: PlatformSubscriptionType;
  /** Current status of the subscription */
  status: PlatformSubscriptionStatus;
  /** Base price for the subscription */
  basePrice: number;
  /** Total monthly price including all charges */
  totalPrice: number;
  /** Number of active barbers (Shop Owner only) */
  activeBarberCount: number;
  /** Number of barbers included in base price (Shop Owner only) */
  includedBarbers: number;
  /** Price per additional barber beyond included count (Shop Owner only) */
  additionalBarberPrice: number;
  /** Number of additional barbers being charged */
  additionalBarbers: number;
  /** Start of current billing period */
  currentPeriodStart: string;
  /** End of current billing period */
  currentPeriodEnd: string;
  /** Trial start date (if applicable) */
  trialStart?: string;
  /** Trial end date (if applicable) */
  trialEnd?: string;
  /** Whether subscription is currently in trial period */
  isInTrial: boolean;
  /** When subscription was cancelled (if applicable) */
  cancelledAt?: string;
  /** When subscription was created */
  createdAt: string;
  /** When subscription was last updated */
  updatedAt: string;
  /** Stripe subscription ID */
  stripeSubscriptionId?: string;
  /** Stripe customer ID */
  stripeCustomerId?: string;
  /** Number of consecutive payment failures */
  paymentFailureCount: number;
  /** When last payment failure occurred */
  lastPaymentFailureAt?: string;
  /** Special notes for this subscription */
  notes?: string;
  /** User ID who owns this subscription */
  userId: string;
  /** User name (computed) */
  userName?: string;
  /** User email (computed) */
  userEmail?: string;
  /** Collection of add-ons attached to this subscription */
  addOns?: SubscriptionAddOnDto[];
  /** Total price of all active add-ons */
  addOnsTotalPrice?: number;
  /** Grand total including base subscription and add-ons */
  grandTotal?: number;
  /** Whether this subscription has AI Analytics add-on active */
  hasAIAnalytics?: boolean;
}

/**
 * Request to create a Shop Owner platform subscription
 */
export interface CreateShopOwnerSubscriptionRequest {
  /** Initial number of barbers for the subscription */
  initialBarberCount: number;
  /** Optional trial period in days (7-14 days) */
  trialDays?: number;
  /** Stripe payment method ID for billing */
  stripePaymentMethodId?: string;
  /** Optional notes for the subscription */
  notes?: string;
}

/**
 * Request to create a Barber platform subscription
 */
export interface CreateBarberSubscriptionRequest {
  /** Optional trial period in days (7-14 days) */
  trialDays?: number;
  /** Stripe payment method ID for billing */
  stripePaymentMethodId?: string;
  /** Optional notes for the subscription */
  notes?: string;
}

/**
 * Request to update barber seats for Shop Owner subscription
 */
export interface UpdateBarberSeatsRequest {
  /** Number of barber seats to add (use positive number) */
  seatsToAdd: number;
  /** Number of barber seats to remove (use positive number) */
  seatsToRemove: number;
  /** Whether to apply proration immediately */
  applyProrationImmediately?: boolean;
}

/**
 * Individual pricing breakdown item
 */
export interface PricingBreakdownItem {
  /** Description of the charge */
  description: string;
  /** Amount for this line item */
  amount: number;
  /** Quantity (for per-seat charges) */
  quantity: number;
}

/**
 * Response DTO for subscription pricing calculation
 */
export interface SubscriptionPricingDto {
  /** Type of subscription being priced */
  type: PlatformSubscriptionType;
  /** Base monthly price */
  basePrice: number;
  /** Number of additional barber seats (Shop Owner only) */
  additionalSeats: number;
  /** Price per additional seat (Shop Owner only) */
  pricePerAdditionalSeat: number;
  /** Additional seats charge */
  additionalSeatsCharge: number;
  /** Total monthly price */
  totalMonthlyPrice: number;
  /** Detailed pricing breakdown */
  breakdown: PricingBreakdownItem[];
}

/**
 * Response DTO for platform subscription statistics
 */
export interface SubscriptionStatisticsDto {
  /** Total number of subscriptions (all types) */
  totalSubscriptions: number;
  /** Number of active subscriptions */
  activeSubscriptions: number;
  /** Number of Shop Owner subscriptions */
  shopOwnerSubscriptions: number;
  /** Number of Barber subscriptions */
  barberSubscriptions: number;
  /** Number of subscriptions in trial period */
  trialSubscriptions: number;
  /** Number of cancelled subscriptions */
  cancelledSubscriptions: number;
  /** Number of past due subscriptions */
  pastDueSubscriptions: number;
  /** Total Monthly Recurring Revenue (MRR) */
  monthlyRecurringRevenue: number;
  /** MRR from Shop Owner subscriptions */
  shopOwnerMRR: number;
  /** MRR from Barber subscriptions */
  barberMRR: number;
  /** Average revenue per Shop Owner subscription */
  averageShopOwnerRevenue: number;
  /** Total number of barber seats across all Shop Owner subscriptions */
  totalBarberSeats: number;
  /** Number of additional barber seats being charged */
  additionalBarberSeats: number;
  /** Revenue from additional barber seats */
  additionalSeatsRevenue: number;
  /** Subscription churn rate (percentage) */
  churnRate: number;
  /** New subscriptions this month */
  newSubscriptionsThisMonth: number;
  /** Subscriptions cancelled this month */
  cancelledThisMonth: number;
  /** MRR growth this month */
  mrrGrowth: number;
  /** Statistics calculation timestamp */
  calculatedAt: string;
}

// ============================================================================
// Barber Membership Plan Types
// ============================================================================

/**
 * Status of a barber membership plan
 */
export enum MembershipPlanStatus {
  /** Plan is active and accepting new subscribers */
  Active = 'Active',
  /** Plan is inactive and not accepting new subscribers */
  Inactive = 'Inactive',
  /** Plan is archived and no longer visible (existing subscribers remain active) */
  Archived = 'Archived'
}

/**
 * DTO for service included in a membership plan
 */
export interface MembershipPlanServiceDto {
  /** Service ID */
  serviceId: number;
  /** Service name */
  serviceName: string;
  /** Service description */
  serviceDescription?: string;
  /** Service price (for reference) */
  servicePrice: number;
  /** Whether this service is included (free) in the plan */
  isIncluded: boolean;
  /** Maximum number of times this service can be used per month (null = unlimited) */
  maxUsagePerMonth?: number;
  /** When this service was added to the plan */
  createdAt: string;
}

/**
 * Response DTO for barber membership plan details
 */
export interface BarberMembershipPlanDto {
  id: number;
  /** Name of the membership plan */
  planName: string;
  /** Detailed description of plan benefits */
  description?: string;
  /** Monthly price for this plan */
  monthlyPrice: number;
  /** Optional discount percentage applied to non-included services (0-100) */
  discountPercentage?: number;
  /** Whether plan is currently active */
  isActive: boolean;
  /** Current plan status */
  status: MembershipPlanStatus;
  /** Whether unused cuts roll over to next month */
  allowRollover: boolean;
  /** Trial period in days (if applicable) */
  trialDays?: number;
  /** Display order for UI sorting */
  displayOrder: number;
  /** Array of highlighted features for UI display */
  highlightedFeatures: string[];
  /** Whether this plan is marked as popular/recommended */
  isPopular: boolean;
  /** Maximum number of subscribers allowed (null = unlimited) */
  maxSubscribers?: number;
  /** Current number of active subscribers */
  currentSubscriberCount: number;
  /** Available slots remaining (null if unlimited) */
  availableSlots?: number;
  /** Whether plan is at capacity */
  isAtCapacity: boolean;
  /** Stripe product ID */
  stripeProductId?: string;
  /** Stripe price ID */
  stripePriceId?: string;
  /** When plan was created */
  createdAt: string;
  /** When plan was last updated */
  updatedAt: string;
  /** ID of barber who owns this plan */
  barberId: number;
  /** Name of barber who owns this plan */
  barberName?: string;
  /** Profile image URL for the barber */
  barberProfileImageUrl?: string;
  /** Associated barbershop ID (null if available at all locations) */
  barbershopId?: number;
  /** Associated barbershop name */
  barbershopName?: string;
  /** Collection of services included in this membership plan */
  includedServices?: MembershipPlanServiceDto[];
  /** Collection of bundles included in this membership plan */
  includedBundles?: MembershipPlanBundleDto[];
  /** Monthly savings vs. standard pricing (if applicable) */
  estimatedMonthlySavings?: number;
}

/**
 * Represents a service to be included in a membership plan
 */
export interface PlanServiceRequest {
  /** ID of the service to add */
  serviceId: number;
  /** Whether the service is included (free) in the plan */
  isIncluded?: boolean;
  /** Maximum number of times this service can be used per month (null = unlimited) */
  maxUsagePerMonth?: number;
}

/**
 * Request to create a new barber membership plan
 */
export interface CreateMembershipPlanRequest {
  /** Name of the membership plan */
  planName: string;
  /** Detailed description of plan benefits */
  description?: string;
  /** Monthly price for this plan */
  monthlyPrice: number;
  /** Optional discount percentage applied to non-included services (0-100) */
  discountPercentage?: number;
  /** Whether plan is active upon creation */
  isActive?: boolean;
  /** Whether unused cuts roll over to next month */
  allowRollover?: boolean;
  /** Trial period in days */
  trialDays?: number;
  /** Display order for UI sorting */
  displayOrder?: number;
  /** Array of highlighted features (max 10 items) */
  highlightedFeatures?: string[];
  /** Mark as popular/recommended plan */
  isPopular?: boolean;
  /** Maximum number of subscribers allowed (null = unlimited) */
  maxSubscribers?: number;
  /** Associated barbershop ID (null = available at all barber's locations) */
  barbershopId?: number;
  /** List of services to include in this plan with their usage limits */
  includedServices?: PlanServiceRequest[];
}

/**
 * Request to update an existing barber membership plan
 */
export interface UpdateMembershipPlanRequest {
  /** Updated plan name */
  planName?: string;
  /** Updated description */
  description?: string;
  /** Updated monthly price (note: may affect existing subscribers) */
  monthlyPrice?: number;
  /** Optional discount percentage applied to non-included services (0-100) */
  discountPercentage?: number;
  /** Whether plan is active */
  isActive?: boolean;
  /** Plan status */
  status?: MembershipPlanStatus;
  /** Whether unused cuts roll over */
  allowRollover?: boolean;
  /** Trial period in days */
  trialDays?: number;
  /** Display order for UI sorting */
  displayOrder?: number;
  /** Highlighted features (replaces existing) */
  highlightedFeatures?: string[];
  /** Popular flag */
  isPopular?: boolean;
  /** Maximum subscribers */
  maxSubscribers?: number;
  /** Whether to apply changes to existing subscribers */
  applyToExistingSubscribers?: boolean;
}

/**
 * Response DTO for membership plan statistics
 */
export interface MembershipPlanStatisticsDto {
  /** Plan ID */
  planId: number;
  /** Plan name */
  planName: string;
  /** Total number of subscribers (all time) */
  totalSubscribers: number;
  /** Currently active subscribers */
  activeSubscribers: number;
  /** Cancelled subscribers */
  cancelledSubscribers: number;
  /** Subscribers in trial period */
  trialSubscribers: number;
  /** Monthly recurring revenue from this plan */
  monthlyRecurringRevenue: number;
  /** Total revenue generated (all time) */
  totalRevenue: number;
  /** Average subscription lifetime in days */
  averageSubscriptionLifetimeDays: number;
  /** Churn rate for this plan (percentage) */
  churnRate: number;
  /** Total cuts redeemed across all subscribers */
  totalCutsRedeemed: number;
  /** Average cuts used per subscriber per month */
  averageCutsUsedPerMonth: number;
  /** Utilization rate (cuts used / cuts available) */
  utilizationRate: number;
  /** New subscribers this month */
  newSubscribersThisMonth: number;
  /** Cancellations this month */
  cancellationsThisMonth: number;
  /** Revenue growth this month */
  revenueGrowth: number;
  /** Statistics calculation timestamp */
  calculatedAt: string;
}

/**
 * Request to subscribe a customer to a membership plan
 */
export interface SubscribeCustomerRequest {
  /** ID of the membership plan to subscribe to */
  planId: number;
  /** Stripe payment method ID for billing */
  stripePaymentMethodId: string;
  /** Optional promotional code */
  promoCode?: string;
  /** Optional notes for the subscription */
  notes?: string;
}

/**
 * Comparison DTO for multiple membership plans
 */
export interface MembershipPlanComparisonDto {
  /** List of plans to compare */
  plans: BarberMembershipPlanDto[];
  /** Comparison metrics */
  comparisonMetrics: Record<string, string>;
  /** Best value plan ID (based on price per cut) */
  bestValuePlanId?: number;
  /** Most popular plan ID */
  mostPopularPlanId?: number;
}

// ============================================================================
// Proration Types
// ============================================================================

/**
 * Type of proration event that triggered the calculation
 */
export enum ProrationEventType {
  /** Shop Owner added barber seats to their subscription */
  BarberSeatAdded = 'BarberSeatAdded',
  /** Shop Owner removed barber seats from their subscription */
  BarberSeatRemoved = 'BarberSeatRemoved',
  /** User upgraded to a higher tier plan */
  PlanUpgrade = 'PlanUpgrade',
  /** User downgraded to a lower tier plan */
  PlanDowngrade = 'PlanDowngrade',
  /** General subscription change (e.g., switching between plans at same tier) */
  SubscriptionChange = 'SubscriptionChange'
}

/**
 * Status of the proration event
 */
export enum ProrationStatus {
  /** Proration has been calculated but not yet applied */
  Pending = 'Pending',
  /** Proration has been successfully applied to invoice */
  Applied = 'Applied',
  /** Proration was refunded or reversed */
  Refunded = 'Refunded',
  /** Proration processing failed */
  Failed = 'Failed'
}

/**
 * Direction of the proration financial impact
 */
export enum ProrationDirection {
  /** Proration results in an immediate charge to customer */
  Charge = 'Charge',
  /** Proration results in a credit applied to next invoice */
  Credit = 'Credit'
}

/**
 * Response DTO for subscription proration details
 */
export interface ProrationDto {
  id: number;
  /** Type of proration event */
  eventType: ProrationEventType;
  /** Current status of the proration */
  status: ProrationStatus;
  /** Direction of proration (Charge or Credit) */
  direction: ProrationDirection;
  /** Prorated amount in USD */
  proratedAmount: number;
  /** Human-readable description of the proration */
  description: string;
  /** When the subscription change occurred */
  eventDate: string;
  /** Start of the billing period being prorated */
  periodStart: string;
  /** End of the billing period being prorated */
  periodEnd: string;
  /** When proration was applied to invoice */
  appliedAt?: string;
  /** When proration record was created */
  createdAt: string;
  /** When proration record was last updated */
  updatedAt: string;
  /** Stripe invoice ID */
  stripeInvoiceId?: string;
  /** Stripe invoice line item ID */
  stripeInvoiceItemId?: string;
  /** Platform subscription ID (if applicable) */
  platformSubscriptionId?: number;
  /** Customer subscription ID (if applicable) */
  customerSubscriptionId?: number;
  /** User ID who initiated the change */
  userId: string;
  /** User name (computed) */
  userName?: string;
  /** Previous state before change */
  previousValue?: string;
  /** New state after change */
  newValue?: string;
  /** Days remaining in period when change occurred */
  daysRemainingInPeriod: number;
  /** Total days in billing period */
  daysInPeriod: number;
  /** Proration factor (0.0 to 1.0) */
  prorationFactor: number;
  /** Whether this is for a platform subscription */
  isPlatformSubscription: boolean;
  /** Whether this is for a customer subscription */
  isCustomerSubscription: boolean;
  /** Whether proration has been applied */
  isApplied: boolean;
  /** Display string for amount with direction (e.g., "+$15.50" or "-$10.25") */
  displayAmount: string;
}

/**
 * Individual calculation step for transparency
 */
export interface CalculationStep {
  /** Step number in sequence */
  stepNumber: number;
  /** Description of calculation step */
  description: string;
  /** Formula or calculation performed */
  formula: string;
  /** Result of this step */
  result: number;
}

/**
 * Response DTO for proration calculation breakdown
 */
export interface ProrationCalculationDto {
  /** Type of event being calculated */
  eventType: ProrationEventType;
  /** Direction of proration (Charge or Credit) */
  direction: ProrationDirection;
  /** Calculated proration amount */
  amount: number;
  /** Human-readable description */
  description: string;
  /** Start of billing period */
  periodStart: string;
  /** End of billing period */
  periodEnd: string;
  /** When change would occur */
  eventDate: string;
  /** Days remaining in period */
  daysRemaining: number;
  /** Total days in period */
  totalDays: number;
  /** Proration factor (0.0 to 1.0) */
  prorationFactor: number;
  /** Old price before change */
  oldPrice: number;
  /** New price after change */
  newPrice: number;
  /** Price difference */
  priceDifference: number;
  /** Previous state description */
  previousValue: string;
  /** New state description */
  newValue: string;
  /** Detailed calculation steps */
  calculationSteps: CalculationStep[];
  /** Whether change will be charged immediately */
  isImmediateCharge: boolean;
  /** When charge/credit will be applied */
  applicationDate?: string;
  /** Display string for UI (e.g., "+$15.50 prorated charge") */
  displaySummary: string;
}

/**
 * Monthly proration trend data
 */
export interface MonthlyProrationTrend {
  /** Month and year */
  month: string;
  /** Number of prorations in month */
  prorationCount: number;
  /** Total charges in month */
  totalCharges: number;
  /** Total credits in month */
  totalCredits: number;
  /** Net impact for month */
  netImpact: number;
}

/**
 * Response DTO for proration statistics
 */
export interface ProrationStatisticsDto {
  /** Total number of proration events (all time) */
  totalProrations: number;
  /** Prorations this month */
  prorationsThisMonth: number;
  /** Total number of charges */
  totalCharges: number;
  /** Total number of credits */
  totalCredits: number;
  /** Total amount charged via prorations */
  totalChargeAmount: number;
  /** Total amount credited via prorations */
  totalCreditAmount: number;
  /** Net proration impact (charges - credits) */
  netProrationImpact: number;
  /** Number of seat additions (Shop Owner) */
  seatAdditions: number;
  /** Number of seat removals (Shop Owner) */
  seatRemovals: number;
  /** Number of plan upgrades */
  planUpgrades: number;
  /** Number of plan downgrades */
  planDowngrades: number;
  /** Average proration charge amount */
  averageChargeAmount: number;
  /** Average proration credit amount */
  averageCreditAmount: number;
  /** Number of pending prorations */
  pendingProrations: number;
  /** Number of applied prorations */
  appliedProrations: number;
  /** Number of failed prorations */
  failedProrations: number;
  /** Breakdown by event type */
  eventTypeBreakdown: Record<ProrationEventType, number>;
  /** Monthly trend data (last 6 months) */
  monthlyTrends: MonthlyProrationTrend[];
  /** Statistics calculation timestamp */
  calculatedAt: string;
}

/**
 * Request to preview proration calculation without applying
 */
export interface PreviewProrationRequest {
  /** Type of event to preview */
  eventType: ProrationEventType;
  /** Subscription ID (platform or customer) */
  subscriptionId: number;
  /** Whether this is a platform subscription (vs customer subscription) */
  isPlatformSubscription?: boolean;
  /** For seat changes: new number of seats */
  newSeatCount?: number;
  /** For plan changes: new plan ID */
  newPlanId?: number;
  /** When change would take effect (defaults to now) */
  effectiveDate?: string;
}

// ============================================================================
// Barber Membership Plan Service Management Types
// ============================================================================

/**
 * Request to add a service to a membership plan
 */
export interface AddServiceToPlanRequest {
  /** Service ID to add */
  serviceId: number;
  /** Whether this service is included (free) in the plan */
  isIncluded: boolean;
  /** Maximum usage per month (null = unlimited) */
  maxUsagePerMonth?: number;
}

/**
 * Request to update service usage limit
 */
export interface UpdateServiceUsageLimitRequest {
  /** New maximum usage per month (null = unlimited) */
  maxUsagePerMonth?: number;
  /** Whether this service is included (free) in the plan */
  isIncluded?: boolean;
}

/**
 * Available service for a barber (not yet added to plan)
 */
export interface AvailableServiceDto {
  /** Service ID */
  id: number;
  /** Service name */
  name: string;
  /** Service description */
  description?: string;
  /** Service price */
  price: number;
  /** Service duration in minutes */
  durationMinutes: number;
  /** Whether service is currently active */
  isActive: boolean;
}

// ============================================================================
// Barber Membership Plan Bundle Types
// ============================================================================

/**
 * DTO for a service bundle included in a membership plan
 */
export interface MembershipPlanBundleDto {
  /** Bundle ID */
  serviceBundleId: number;
  /** Bundle name */
  bundleName: string;
  /** Bundle description */
  bundleDescription?: string;
  /** Final price of the bundle */
  bundlePrice: number;
  /** Original price before bundle discount */
  originalPrice: number;
  /** Amount saved with bundle discount */
  discountApplied: number;
  /** Number of services in the bundle */
  serviceCount: number;
  /** Names of services included in the bundle */
  serviceNames: string[];
  /** Whether this bundle is included (free) in the plan */
  isIncluded: boolean;
  /** Maximum number of times this bundle can be used per month (null = unlimited) */
  maxUsagePerMonth?: number;
  /** Whether to track usage at bundle level (true) or service level (false) */
  trackAtBundleLevel: boolean;
  /** When this bundle was added to the plan */
  createdAt: string;
}

/**
 * Request to add a bundle to a membership plan
 */
export interface AddBundleToPlanRequest {
  /** Service bundle ID to add */
  serviceBundleId: number;
  /** Whether bundle is included (free) in the plan */
  isIncluded?: boolean;
  /** Max usage per month (null = unlimited) */
  maxUsagePerMonth?: number;
  /** Track at bundle level vs service level */
  trackAtBundleLevel?: boolean;
}

/**
 * Request to update a bundle in a membership plan
 */
export interface UpdateBundleInPlanRequest {
  /** Whether bundle is included (free) in the plan */
  isIncluded?: boolean;
  /** Max usage per month (null = unlimited) */
  maxUsagePerMonth?: number;
  /** Track at bundle level vs service level */
  trackAtBundleLevel?: boolean;
}

/**
 * Available bundle for a barber (not yet added to plan)
 */
export interface AvailableBundleDto {
  /** Bundle ID */
  id: number;
  /** Bundle name */
  name: string;
  /** Bundle description */
  description?: string;
  /** Final bundle price */
  bundlePrice: number;
  /** Original price before discount */
  originalPrice: number;
  /** Type of discount (PercentageDiscount, FixedPrice, etc.) */
  discountType: string;
  /** Amount saved */
  discountValue: number;
  /** Number of services in bundle */
  serviceCount: number;
  /** Names of services included */
  serviceNames: string[];
  /** Whether bundle is active */
  isActive: boolean;
  /** Whether bundle is already in a plan */
  isAlreadyInPlan: boolean;
}

// ============================================================================
// Subscription Add-On Types
// ============================================================================

/**
 * Type of subscription add-on
 */
export enum AddOnType {
  /** AI Analytics and Advanced Insights */
  AIAnalytics = 'AIAnalytics',
  /** Advanced Reporting (Future) */
  AdvancedReporting = 'AdvancedReporting',
  /** Priority Support (Future) */
  PrioritySupport = 'PrioritySupport',
  /** Marketing Automation (Future) */
  MarketingAutomation = 'MarketingAutomation',
  /** Inventory Management System */
  Inventory = 'Inventory'
}

/**
 * Status of a subscription add-on
 */
export enum AddOnStatus {
  /** Add-on is active and functional */
  Active = 'Active',
  /** Add-on has been cancelled but still active until period end */
  Cancelled = 'Cancelled',
  /** Add-on is in trial period */
  Trialing = 'Trialing',
  /** Add-on is suspended due to payment issues */
  Suspended = 'Suspended',
  /** Add-on has expired and is no longer active */
  Expired = 'Expired'
}

/**
 * Response DTO for subscription add-on details
 */
export interface SubscriptionAddOnDto {
  id: number;
  /** Type of add-on */
  type: AddOnType;
  /** Current status of the add-on */
  status: AddOnStatus;
  /** Name of the add-on */
  name: string;
  /** Description of add-on features */
  description?: string;
  /** Regular monthly price */
  regularPrice: number;
  /** Current price being charged (may be promotional or zero) */
  currentPrice: number;
  /** Whether this add-on is currently free */
  isFree: boolean;
  /** Promotional message */
  promotionalMessage?: string;
  /** When promotional pricing ends */
  promotionalEndDate?: string;
  /** Start of current billing period */
  currentPeriodStart: string;
  /** End of current billing period */
  currentPeriodEnd: string;
  /** Trial start date */
  trialStart?: string;
  /** Trial end date */
  trialEnd?: string;
  /** Whether add-on is in trial */
  isInTrial: boolean;
  /** When add-on was activated */
  activatedAt: string;
  /** When add-on was cancelled */
  cancelledAt?: string;
  /** Stripe subscription item ID */
  stripeSubscriptionItemId?: string;
  /** Platform subscription ID this add-on belongs to */
  platformSubscriptionId: number;
  /** Display price with promotional context */
  displayPrice: string;
}

/**
 * Request to add an add-on to a subscription
 */
export interface AddSubscriptionAddOnRequest {
  /** Type of add-on to add */
  type: AddOnType;
  /** Optional trial period in days */
  trialDays?: number;
  /** Optional notes */
  notes?: string;
}

/**
 * Response DTO for available add-ons catalog
 */
// ============================================================================
// Membership Checkout (Stripe hosted Checkout)
// ============================================================================

/**
 * Request body for POST /barber-membership-plans/{planId}/checkout
 */
export interface MembershipCheckoutRequest {
  /** URL to redirect to after successful payment */
  successUrl: string;
  /** URL to redirect to if payment is cancelled */
  cancelUrl: string;
  /**
   * Optional gift card code (REV-10). Applied as a coupon to the first
   * invoice only; the backend is authoritative on validity/redeemability.
   */
  giftCardCode?: string;
}

/**
 * Response from POST /barber-membership-plans/{planId}/checkout
 */
export interface MembershipCheckoutResponse {
  /** Stripe Checkout URL to redirect the browser to */
  checkoutUrl: string;
  /** Stripe Checkout session ID */
  sessionId: string;
}

export interface AvailableAddOnDto {
  /** Type of add-on */
  type: AddOnType;
  /** Name of the add-on */
  name: string;
  /** Description of features */
  description: string;
  /** Regular monthly price */
  regularPrice: number;
  /** Current promotional price (if applicable) */
  promotionalPrice?: number;
  /** Whether currently available for free */
  isFree: boolean;
  /** Promotional message */
  promotionalMessage?: string;
  /** When promotion ends */
  promotionalEndDate?: string;
  /** Whether user already has this add-on */
  isSubscribed: boolean;
  /** Features included in this add-on */
  features: string[];
  /** Display price */
  displayPrice: string;
}
